/** Shared authoring contract. Only these fields can enter a public listing. */
export const LISTING_KINDS = [
  "fbo",
  "flight_school",
  "maintenance",
  "service_provider",
] as const;
export type ListingKind = (typeof LISTING_KINDS)[number];
export type ListingField = {
  key: string;
  label: string;
  type: "text" | "email" | "url" | "list" | "boolean" | "integer";
  max?: number;
  required?: boolean;
  visibility: "public" | "private" | "review";
};
const text = (key: string, label: string, max = 200): ListingField => ({
  key,
  label,
  type: "text",
  max,
  visibility: "public",
});
const list = (key: string, label: string): ListingField => ({
  key,
  label,
  type: "list",
  max: 50,
  visibility: "public",
});
const flag = (key: string, label: string): ListingField => ({
  key,
  label,
  type: "boolean",
  visibility: "public",
});
export const LISTING_COMMON_FIELDS: readonly ListingField[] = [
  { ...text("name", "Business name"), required: true },
  text("description", "Description", 8000),
  text("phone", "Public phone", 40),
  { ...text("email", "Public email", 254), type: "email" },
  { ...text("website", "Website", 2048), type: "url" },
  text("address", "Street address", 500),
  text("country", "Country code", 2),
  {
    ...text("contactName", "Review contact name"),
    visibility: "private",
    required: true,
  },
  {
    ...text("contactEmail", "Review contact email", 254),
    type: "email",
    visibility: "private",
    required: true,
  },
  {
    ...text("verificationNotes", "Ownership / certification evidence", 4000),
    visibility: "review",
  },
];
export const LISTING_CATEGORY_FIELDS: Record<
  ListingKind,
  readonly ListingField[]
> = {
  fbo: [
    { ...text("airportCode", "Airport identifier", 8), required: true },
    text("street", "Street", 200),
    text("postalCode", "Postal code", 20),
    text("hoursText", "Hours", 500),
    text("unicomFreq", "UNICOM", 30),
    text("fuelBrand", "Fuel brand", 100),
    text("fuelingHours", "Fueling hours", 500),
    list("amenities", "Amenities"),
    list("services", "Services"),
    list("aircraftCapabilities", "Aircraft handling"),
    list("accreditations", "Accreditations (subject to review)"),
    list("acceptedPaymentMethods", "Payment methods"),
    flag("safAvailable", "Sustainable aviation fuel"),
    flag("open24Hours", "Open 24 hours"),
    flag("afterHoursCallout", "After-hours callout"),
    text("afterHoursPhone", "After-hours phone", 40),
    text("afterHoursNote", "After-hours details", 1000),
  ],
  flight_school: [
    { ...text("airportCode", "Airport identifier", 8), required: true },
    text("hoursText", "Hours", 500),
    list("programCategories", "Programs"),
    list("trainingTypes", "Training types"),
    list("financingOptions", "Financing options"),
    list("accreditations", "Accreditations (subject to review)"),
  ],
  maintenance: [
    { ...text("airportCode", "Airport identifier", 8), required: true },
    { ...text("primaryCategory", "Primary discipline"), required: true },
    ...[
      "secondaryCategories",
      "capabilities",
      "aircraftCategories",
      "manufacturers",
      "models",
      "enginePlatforms",
      "avionicsBrands",
      "certifications",
      "locationModes",
      "servedAirportCodes",
      "coverageRegions",
      "coverageStates",
      "coverageCountries",
      "enabledRequestTypes",
    ].map((key) => list(key, key.replace(/([A-Z])/g, " $1"))),
    text("coverageType", "Coverage type", 30),
    text("hoursText", "Hours", 500),
    {
      key: "serviceRadiusNm",
      label: "Service radius (NM)",
      type: "integer",
      max: 20000,
      visibility: "public",
    },
    flag("aogAvailable", "AOG available"),
    flag("available247", "Available 24/7"),
  ],
  service_provider: [
    { ...text("primaryCategory", "Primary service category"), required: true },
    ...[
      "secondaryCategories",
      "capabilities",
      "aircraftCategories",
      "certifications",
      "leadTypes",
      "coverageRegions",
      "coverageStates",
      "coverageCountries",
      "servedAirportCodes",
      "serviceAvailabilityModes",
    ].map((key) => list(key, key.replace(/([A-Z])/g, " $1"))),
    text("baseAirportCode", "Base airport identifier", 8),
    text("city", "City"),
    text("state", "State / region"),
    text("postalCode", "Postal code", 20),
    text("coverageType", "Coverage type", 30),
    {
      key: "serviceRadiusNm",
      label: "Service radius (NM)",
      type: "integer",
      max: 20000,
      visibility: "public",
    },
  ],
};
export type ListingDraftValues = Record<
  string,
  string | string[] | boolean | number
>;
export function listingFields(kind: ListingKind): readonly ListingField[] {
  return [...LISTING_COMMON_FIELDS, ...LISTING_CATEGORY_FIELDS[kind]];
}
export function validateListingValues(
  kind: ListingKind,
  input: unknown,
  complete: boolean,
): {
  values: ListingDraftValues;
  errors: Record<string, string>;
  completeness: number;
} {
  const values: ListingDraftValues = {},
    errors: Record<string, string> = {};
  const fields = listingFields(kind);
  if (!input || typeof input !== "object" || Array.isArray(input))
    return {
      values,
      errors: { values: "Expected listing fields" },
      completeness: 0,
    };
  const raw = input as Record<string, unknown>;
  for (const key of Object.keys(raw))
    if (!fields.some((f) => f.key === key))
      errors[key] = "Unsupported listing field";
  for (const field of fields) {
    const value = raw[field.key];
    if (value === undefined || value === "") {
      if (complete && field.required)
        errors[field.key] = `${field.label} is required`;
      continue;
    }
    if (field.type === "boolean") {
      if (typeof value === "boolean") values[field.key] = value;
      else errors[field.key] = "Expected yes or no";
    } else if (field.type === "integer") {
      if (
        typeof value === "number" &&
        Number.isSafeInteger(value) &&
        value >= 0 &&
        value <= field.max!
      )
        values[field.key] = value;
      else errors[field.key] = "Invalid nonnegative whole number";
    } else if (field.type === "list") {
      if (
        !Array.isArray(value) ||
        value.length > field.max! ||
        value.some((v) => typeof v !== "string" || !v.trim() || v.length > 200)
      )
        errors[field.key] = "Use a bounded list of text values";
      else values[field.key] = [...new Set(value.map((v) => v.trim()))];
    } else if (
      typeof value !== "string" ||
      value.length > field.max! ||
      /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(value)
    )
      errors[field.key] = "Invalid or oversized text";
    else {
      const trimmed = value.trim();
      if (!trimmed && complete && field.required)
        errors[field.key] = `${field.label} is required`;
      if (
        field.type === "email" &&
        trimmed &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
      )
        errors[field.key] = "Enter a valid email";
      if (
        field.type === "url" &&
        trimmed &&
        !/^https?:\/\/[a-z\d](?:[a-z\d.-]*[a-z\d])?(?::\d{1,5})?(?:[/?#][^\s]*)?$/i.test(
          trimmed,
        )
      )
        errors[field.key] =
          "Enter a public HTTP(S) website without credentials";
      values[field.key] = [
        "airportCode",
        "baseAirportCode",
        "country",
      ].includes(field.key)
        ? trimmed.toUpperCase()
        : trimmed;
    }
  }
  const required = fields.filter((f) => f.required);
  return {
    values,
    errors,
    completeness: Math.round(
      (required.filter(
        (f) =>
          typeof values[f.key] === "string" && (values[f.key] as string).trim(),
      ).length /
        required.length) *
        100,
    ),
  };
}
export function publicListingValues(
  kind: ListingKind,
  values: ListingDraftValues,
): ListingDraftValues {
  return Object.fromEntries(
    listingFields(kind)
      .filter((f) => f.visibility === "public" && values[f.key] !== undefined)
      .map((f) => [f.key, values[f.key]]),
  );
}
